import React, { useCallback, useEffect, useRef } from 'react';

import { OrgChartEmployee } from '../interfaces';
import AnimatedOrgChartConnector from './AnimatedOrgChartConnector';
import OrgChartCompanyReportsSection from './CompanyReportsSection';
import OrgChartEmployeeTopLevel from './EmployeeTopLevel';
import useOrgChart from './OrgChartContext';

const OrgChartCompanyTree = () => {
  const directReportsRef = useRef(null);
  const employeeRefs = useRef({});
  const { employees, selectedEmployee, rootEmployee, expandToEmployee } = useOrgChart();

  const firstLevelWithReports = employees.reduce((minLevel, employee) => {
    if (employee.reports && employee.reports.length > 0) {
      return minLevel === null ? employee.level : Math.min(minLevel, employee.level);
    }
    return minLevel;
  }, Infinity);

  const employeesByLevelBeforeFirstLevelWithReports: OrgChartEmployee[][] = [];

  for (let level = 0; level <= firstLevelWithReports; level++) {
    employeesByLevelBeforeFirstLevelWithReports[level] = employees.filter(
      (employee) => employee.level === level
    );
  }

  const setSelectedEmployeeToFirstLevelWithReports = useCallback(() => {
    const firstEmployeeWithReports = employees.find((employee) => employee.reports?.length > 0);
    if (firstEmployeeWithReports) {
      expandToEmployee(firstEmployeeWithReports.id);
    }
  }, [employees, expandToEmployee]);

  useEffect(() => {
    if (selectedEmployee === null) {
      setSelectedEmployeeToFirstLevelWithReports();
    }
  }, [selectedEmployee, employees, setSelectedEmployeeToFirstLevelWithReports]);

  const setEmployeeRef = (id: string, element: HTMLElement | null) => {
    if (element) {
      employeeRefs.current[id] = element;
    }
  };

  const reportsByEmployeeId = employees.reduce(
    (acc, employee) => {
      if (employee.reports) {
        acc[employee.id] = employee.reports.map((reportId) =>
          employees.find((e) => e.id === reportId)
        );
      }
      return acc;
    },
    {} as Record<string, OrgChartEmployee[]>
  );

  return (
    <div className="relative mx-auto max-w-container-lg">
      <div className="flex flex-col items-center justify-center">
        {employeesByLevelBeforeFirstLevelWithReports.map((employees, level) => (
          <div key={level} className="mb-8 flex flex-row flex-wrap justify-center gap-24">
            {employees.map((employee) => (
              <div
                key={employee.id}
                ref={(el) => setEmployeeRef(employee.id, el)}
                className="relative"
              >
                <OrgChartEmployeeTopLevel
                  name={employee.name}
                  position={employee.title}
                  imageUrl={employee.imageUrl}
                  reports={reportsByEmployeeId[employee.id] ?? []}
                  level={employee.level}
                  isSelected={(rootEmployee?.id ?? '') === employee.id}
                  id={employee.id}
                  isRootLevel={true}
                />
              </div>
            ))}
          </div>
        ))}

        {rootEmployee && (
          <AnimatedOrgChartConnector
            selectedEmployeeId={rootEmployee.id}
            reportsSectionRef={directReportsRef}
            employees={employees}
          />
        )}

        <div
          ref={directReportsRef}
          className="mt-48 h-40 w-full border-b-0 border-l-2 border-r-2 border-t-2 border-solid border-primary-magenta"
        >
          <p className="mx-auto mb-0 mt-4 text-center font-suisse-intl-mono text-caption-sm text-grayscale-gray">
            {selectedEmployee &&
              employees.find((employee) => employee.id === rootEmployee.id)?.name.toString() +
                '’s direct reports'}
          </p>
        </div>

        {rootEmployee && (
          <OrgChartCompanyReportsSection employees={employees} employeeWithReports={rootEmployee} />
        )}
      </div>
    </div>
  );
};

export default OrgChartCompanyTree;
